import { useEffect, useMemo } from "react";
import { Avatar, Box, Grid, Paper, Stack, Typography } from "@mui/material";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import { EmptyState } from "@/core/ui";
import SummaryCard from "./components/SummaryCard";
import useDashboard from "./hooks/useDashboard";

const Dashboard = () => {
  const { stats, recentMatches, fetchDashboard } = useDashboard();

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const summaryData = useMemo(
    () => [
      {
        id: "players",
        value: stats?.totalPlayers ?? "-",
        label: "Jogadores",
        subLabel: "Cadastrados",
      },
      {
        id: "seasons",
        value: stats?.totalRankings ?? "-",
        label: "Rankings",
        subLabel: "Existentes",
      },
      {
        id: "seasons",
        value: stats?.totalRounds ?? "-",
        label: "Rodadas",
        subLabel: "Existentes",
      },
    ],
    [stats],
  );

  return (
    <Stack spacing={2.5}>
      <Typography
        variant="h4"
        sx={{
          fontWeight: 900,
          letterSpacing: 0.8,
          color: "text.primary",
          lineHeight: 1.05,
        }}
      >
        Dashboard Admin
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={{ mt: 0.8, fontWeight: 600 }}
      >
        Visão geral da competição, status da rodada e últimas partidas
        reportadas.
      </Typography>

      <Box sx={{ overflow: "hidden" }}>
        <Grid container spacing={2}>
          {summaryData.map((item) => (
            <SummaryCard key={item.id} item={item} />
          ))}
        </Grid>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Partidas recentes
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          Últimas partidas enviadas (3 jogos por rodada)
        </Typography>

        {recentMatches.length === 0 && (
          <EmptyState
            icon={<SportsTennisIcon sx={{ fontSize: 34 }} />}
            title="Nenhuma partida registrada ainda"
            description="As partidas aparecerão aqui conforme forem sendo reportadas."
          />
        )}

        <Stack spacing={1.6}>
          {recentMatches.map((categoryRow) => (
            <Paper
              key={categoryRow.category.id}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 1.5,
                p: 1.4,
                backgroundColor: "background.default",
              }}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                spacing={0.5}
                sx={{ mb: 1.2 }}
              >
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 800, color: "primary.main" }}
                >
                  {categoryRow.category.name} -{" "}
                  {categoryRow.category.type === "MASC"
                    ? "Masculino"
                    : "Feminino"}{" "}
                  | {categoryRow.matches[0].ranking.name}
                </Typography>
              </Stack>

              <Grid container spacing={1.2}>
                {categoryRow.matches.map((matchItem) => (
                  <Grid item xs={12} sm={4} key={matchItem.id}>
                    <Paper
                      elevation={0}
                      sx={{
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1,
                        backgroundColor: "background.paper",
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "minmax(0, 1fr) auto minmax(0, 1fr)",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Stack spacing={0.6}>
                          {matchItem.teamA.players.map((player) => (
                            <Stack
                              key={player.id}
                              direction="row"
                              spacing={0.7}
                              alignItems="center"
                            >
                              <Avatar
                                sx={{ width: 22, height: 22, fontSize: 12 }}
                              >
                                {player.name[0]}
                              </Avatar>
                              <Typography
                                noWrap
                                variant="caption"
                                sx={{ fontWeight: 700 }}
                              >
                                {player.name}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>

                        <Typography
                          sx={{
                            fontSize: { xs: 34, lg: 42 },
                            fontWeight: 900,
                            lineHeight: 1,
                            color: "text.primary",
                          }}
                        >
                          {matchItem.teamA.score} X {matchItem.teamB.score}
                        </Typography>

                        <Stack spacing={0.6}>
                          {matchItem.teamB.players.map((player) => (
                            <Stack
                              key={player.id}
                              direction="row"
                              spacing={0.7}
                              alignItems="center"
                              justifyContent="flex-end"
                            >
                              <Typography
                                noWrap
                                variant="caption"
                                sx={{ fontWeight: 700 }}
                              >
                                {player.name}
                              </Typography>
                              <Avatar
                                sx={{ width: 22, height: 22, fontSize: 12 }}
                              >
                                {player.name[0]}
                              </Avatar>
                            </Stack>
                          ))}
                        </Stack>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};

export default Dashboard;
